import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import { getFormValues } from 'redux-form';
import moment from 'moment';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import find from 'lodash/find';
import _upperFirst from 'lodash/upperFirst';
import get from 'lodash/get';
import qs from 'qs';
import csv from 'csv-stringify';

import intl from '../../shared/intl';
import endpoints from '../../../config/endpoints';
import http from '../../shared/http';
import createCalendar from '../../shared/calendar';
import createFilters from '../../shared/filters';
import createTable from '../components/table/conversions';
import createChangeStatusForm from '../components/change-status-form';
import getRequestQuery from '../shared/get-request-query';
import listOfFilters from '../shared/filters';
import checkPermissions from '../../shared/check-permissions';
import getFilterContext from '../shared/get-filters-context';
import { regions } from './../../../../../../enums/regions';
import popupModule from '../components/popups';

const isEverad = process.env.CPA_ENV === 'everad';

const changeStatusForm = createChangeStatusForm();

const calendar = createCalendar({
	namespace: 'conversions',
	stateNodeName: 'calendar',
	defaultValue: {
		start: moment().format('YYYY-MM-DD'),
		end: moment().format('YYYY-MM-DD')
	}
});

const filters = createFilters({
	namespace: 'conversions',
	stateNodeName: 'filters',
	queryBuildFunc: getRequestQuery,
	listOfFilters: [].concat(listOfFilters, [{
		name: 'ids',
		title: 'ID',
		placeholder: 'Add ID',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.STATISTICS.FILTERS.IDS
		}
	}, {
		name: 'crm_id',
		title: 'Crm ids',
		placeholder: 'Add ID',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.STATISTICS.FILTERS.CRM_IDS
		}
	},
	{
		name: 'statuses',
		title: 'Status',
		placeholder: 'Add status',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'static',
			source: 'root.lists.conversion-statuses'
		}
	}, {
		name: 'tags',
		title: 'Tags',
		placeholder: 'Add affiliate\'s tag',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: false,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.AFFILIATE_TAGS
		}
	}, {
		name: 'category',
		title: 'Category',
		placeholder: 'Add category',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: isEverad ? endpoints.LOOKUPS.OFFERS_CATEGORIES : endpoints.LOOKUPS.OFFERS_CATEGORIES_RP
		}
	}, {
		name: 'niche',
		title: 'Niches',
		placeholder: 'Add niche',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.OFFERS_NICHES
		}
	}, {
		name: 'regions',
		title: 'Regions',
		placeholder: 'Add regions',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: true,
		options: {
			type: 'provided',
			source: regions.map(reg => ({
				value: reg,
				label: reg
			}))
		}
	}, {
		name: 'conversion_names',
		title: 'Conversion names',
		placeholder: 'Conversion names',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: false,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.CONVERSION_NAMES
		}
	}, {
		name: 'conversion_phones',
		title: 'Conversion phones',
		placeholder: 'Conversion phones',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: false,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.CONVERSION_PHONES
		}
	}, {
		name: 'cancellation_reasons',
		title: 'Cancellation reasons',
		placeholder: 'Add reason',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: false,
		options: {
			type: 'static',
			source: 'root.lists.conversion-cancellation-reasons'
		}
	}, {
		name: 'sids',
		title: 'sids',
		placeholder: 'Sids',
		value: [],
		multi: true,
		shouldInverse: false,
		shouldShowInverseControl: false,
		options: {
			type: 'dynamic',
			source: endpoints.LOOKUPS.SIDS
		}
	}])
});

const table = createTable({
	namespace: 'conversions',
	stateNodeName: 'table'
});

// actions types
const FETCH_DATA_SUCCESS = 'conversions/fetch-data-success';
const FETCH_DATA_ERROR = 'conversions/fetch-data-error';

const SHOW_PROGRESS_BAR = 'conversions/show-progress-bar';
const CLEAR = 'conversions/clear';

const SELECT_CURRENCY = 'conversions/select-currency';
const SET_DATA_USE_EXTERNAL_QUERY = `conversions/set-data-use-external-query`;

const SEARCH_CRM_ID = `conversions/search-crm-id`;
const SEARCH_CPA_ID = `conversions/search-cpa-id`;

const MARK_AS_FRAUD = `conversions/mark-as-fraud`;

// permissions
export const allowedPermissions = {
	'view': 'analytics_view',
	'analytics_view_extended_stat': 'analytics_view_extended_stat',
	'change_conversion_statuses': 'change_conversion_statuses',
	'autotrash_view': 'autotrash_view',
	'mark_as_fraud': 'payouts_approve'
};

// initial state
const initialState = {
	data: null,
	showProgressBar: false,
	calendar: calendar.initialState,
	filters: filters.initialState,
	table: table.initialState,
	searchCrmIdText: '',
	searchCpaIdText: '',
	query: {
		date_range: {
			start: null,
			end: null
		}
	},
	...changeStatusForm.initialState,
	error: false
};

// actions
export const actions = Object.assign({
	fetchData: props => (dispatch, getState) => {
		const externalQuery = (props.location && props.location.query && qs.parse(props.location.query)) || {};
		const savedFilters = filters.selectors.getSavedFilters(getState(), 'conversions');
		const externalConvId = parseInt(externalQuery.id);

		if (
			externalConvId
			&& !selectors.getSelectedIdsInFilters(getState()).includes(externalConvId)
		) {
			const idFilter = [{ value: externalConvId, label: externalConvId }];
			dispatch(filters.actions.addFilter('ids'));
			dispatch(filters.actions.addFilterValue(idFilter, 'ids'));
			dispatch(table.actions.toggleDetails(externalConvId));
		}

		if (externalConvId) {
			externalQuery.query = { ...externalQuery.query, external_conv_id: externalConvId };
		}

		if (isEmpty(externalQuery) && savedFilters.entity && savedFilters.entity.filters.length) {
			return dispatch(replace(`statistics/conversions/?${qs.stringify(savedFilters)}`));
		}

		dispatch(actions.fetchRequest(externalQuery)).then(action => {
			if (externalQuery.entity) {
				const filtersList = externalQuery.entity.filters ? map(externalQuery.entity.filters, f => {
					return {
						name: f.name,
						values: map(f.values, v => ({
							label: v.label,
							value: Number(v.value) || v.value
						}))
					};
				}) : [];

				dispatch(filters.actions.setFiltersFromExternalQuery(filtersList, false));

				dispatch({
					type: SET_DATA_USE_EXTERNAL_QUERY,
					payload: externalQuery.entity
				});
			}
		});
	},

	fetchRequest: (externalQuery = {}) => (dispatch, getState) => {

		const query = getRequestQuery(
			getState().conversions,
			externalQuery && externalQuery['query']
		);
		dispatch(actions.showProgressBar());

		return (http({
			url: `${endpoints.STATISTICS.CONVERSIONS}?${query}`,
			method: 'get',
			actions: [
				FETCH_DATA_SUCCESS,
				FETCH_DATA_ERROR
			]
		}))(dispatch);
	},

	apply: () => dispatch => {
		dispatch(actions.showProgressBar());
		dispatch(actions.fetchRequest());
	},

	clear: () => dispatch =>  {
		dispatch(replace('statistics/conversions/'));
		dispatch({
			type: CLEAR
		});
	},

	selectCurrency: id => dispatch => {
		dispatch({
			type: SELECT_CURRENCY,
			payload: id
		});

		dispatch(actions.apply());
	},

	showProgressBar: () => ({
		type: SHOW_PROGRESS_BAR
	}),

	markAsFraud: () => (dispatch, getState) => {
		const conversion_ids = selectors.getData(getState()).map(i => i.id);
		const affiliate_ids = selectors.getAffiliatesFilterIds(getState());

		const request = http({
			url: endpoints.CONVERSIONS.MARK_FRAUD,
			method: 'put',
			data: { conversion_ids, affiliate_ids },
			actions: [ MARK_AS_FRAUD ]
		});

		return request(dispatch);
	},
	extendedStatExport: () => dispatch => dispatch(actions.statExport({ isExtended: true })),
	statExport: ({ isExtended = false}) => (dispatch, getState) => {
		const { rows } = selectors.getTable(getState(), true);
		const cancellationReasons = getState().root.lists['conversion-cancellation-reasons'];

		if (rows.length === 0) return;

		const headers = [
			'id', 'date', 'sid1', 'sid2', 'sid3', 'sid4', 'sid5',
			'offer', 'country', 'country_code', 'city', 'ip',
			'status', 'cancellation_reason', 'payout', 'currency',
			'campaign', 'campaign_id', 'campaign_type', 'click_referer',
			'contacts', 'first_called_at', 'calls_count', 'status_changed_at',
			'referer', 'landing', 'transit', 'client_age', 'client_gender', 'is_redeemed',
			'crm_id', 'comment', 'chatbot_history', 'affiliate'
		];

		if (isExtended) {
			headers.push('internal_name', 'internal_phone');
		}

		const columns = rows.map(entity => {
			return headers.map(index => {
				if (index === 'cancellation_reason') {
					const value = entity[index] ? entity[index] : '';
					const cancellationReason = find(cancellationReasons, { id: _upperFirst(value) });
					return cancellationReason ? cancellationReason['text'] : value;
				}
				if (index === 'comment') {
					return entity.extra_fields && entity.extra_fields[index];
				}
				return entity[index] || '-';
			});
		});

		const rowsForExport = [].concat([headers], columns);

		csv(rowsForExport, (err, res) => {
			const exportFilename = `Export--Convertions--${moment().format(
				'YYYY-MM-DD--HH-mm-ss'
			)}.csv`;
			const csvData = new Blob([res], { type: 'text/csv;charset=utf-8;' });
			//IE11 & Edge
			if (navigator.msSaveBlob) {
				navigator.msSaveBlob(csvData, exportFilename);
			} else {
				//In FF link must be added to DOM to be clicked
				const link = document.createElement('a');
				link.href = window.URL.createObjectURL(csvData);
				link.setAttribute('download', exportFilename);
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
			}
		});
	},

	searchCrmId: () => (dispatch, getState) => {
		const searchForm = getFormValues('conversionsCrmIdSearchForm')(getState());
		const search = searchForm && searchForm.search || '';

		dispatch({
			type: SEARCH_CRM_ID,
			payload: search
		});
	},
	searchCpaId: () => (dispatch, getState) => {
		const searchForm = getFormValues('conversionsCpaIdSearchForm')(getState());
		const search = searchForm && searchForm.search || '';

		dispatch({
			type: SEARCH_CPA_ID,
			payload: search
		});
	}

},  calendar.actions,
filters.actions,
popupModule.actions,
changeStatusForm.actions,
table.actions
);

// reducers
const reducers = Object.assign({
	[LOCATION_CHANGE]: () => {
		return initialState;
	},
	[FETCH_DATA_SUCCESS]: (state, action) => ({
		...state, data: action.payload.data, showProgressBar: false, error: false
	}),
	[FETCH_DATA_ERROR]: (state, action) => ({ ...state, error: true, showProgressBar: false }),
	[SHOW_PROGRESS_BAR]: state => ({...state, showProgressBar: true}),
	[CLEAR]: state => ({
		...initialState,
		query: {
			...initialState.query,
			currency: state.query.currency
		}
	}),
	[SELECT_CURRENCY]: (state, action) => ({
		...state,
		query: {
			...state.query,
			currency: action.payload
		}
	}),
	[SET_DATA_USE_EXTERNAL_QUERY]: (state, action) => {
		const { currency, date_range } = action.payload;

		if (!date_range) {
			return state;
		}

		return {
			...state,
			query: {
				currency
			},
			calendar: {
				...state.calendar,
				value: date_range
			}
		};
	},
	[SEARCH_CRM_ID]: (state, action) => ({
		...state,
		searchCrmIdText: action.payload
	}),
	[SEARCH_CPA_ID]: (state, action) => ({
		...state,
		searchCpaIdText: action.payload
	})
},
calendar.reducers,
filters.reducers,
changeStatusForm.reducers,
popupModule.reducers,
table.reducers
);
export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.conversions.data,
	getIntl: intl,
	getTable: (state, usePagination) => table.selectors.getTable(state, usePagination),
	getCalendar: state => calendar.selectors.getCalendar(
		state => state.conversions.calendar,
		state => state
	)(state),
	getFilters: state => filters.selectors.getFilters(
		state => state.conversions.filters,
		state => state
	)(state),
	getAffiliatesFilterValues: state => {
		const filters = selectors.getFilters(state) || {};
		const affiliatesFilter = find(filters.selectedFilters, { name: 'affiliates' });
		if (!get(affiliatesFilter, 'value.length')) {
			return [];
		}
		return affiliatesFilter.value.map(val => val.label);
	},
	getAffiliatesFilterIds: state => {
		const filters = selectors.getFilters(state) || {};
		const affiliatesFilter = find(filters.selectedFilters, { name: 'affiliates' });
		if (!get(affiliatesFilter, 'value.length')) {
			return [];
		}
		return affiliatesFilter.value.map(val => val.value);
	},
	getSelectedIdsInFilters: state => {
		const { selectedFilters } = selectors.getFilters(state);
		const idsFilter = find(selectedFilters, { name: 'ids' });

		return get(idsFilter, 'value.length')
			? map(idsFilter.value, val => val.value)
			: [];
	},
	getListOfCurrencies: state => {
		const listOfCurrency = state.root.lists['available-currencies'];
		const all = { id: undefined, text: 'All' };
		return [all, ...listOfCurrency];
	},
	getListOfRefererTypes: state => state.root.lists['referer-types'],
	getCurrentCurrency: state => state.conversions.query.currency,
	getProgressBar: state => state.conversions.showProgressBar,
	getIsStatusChangeFormShowed: state => changeStatusForm.selectors.getIsStatusChangeFormShowed(state),
	getConversionStatusOptions: state => changeStatusForm.selectors.getConversionStatusOptions(state),
	getConversionCancellationReasons: state => changeStatusForm.selectors.getConversionCancellationReasons(state),
	getSearchCrmIdInitialValues: state => ({
		search: state.conversions.searchCrmIdText
	}),
	getSearchCpaIdInitialValues: state => ({
		search: state.conversions.searchCpaIdText
	}),
	getFilterContext: state => getFilterContext(state.conversions),
	getCan: state => checkPermissions(state, allowedPermissions),
	getError: state => state.conversions.error,
	...popupModule.selectors
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/statistics/conversions/index.js